import React from 'react'
import {Button, Code, Popover, Stack, Table, Text} from '@mantine/core'
import dayjs from 'dayjs'
import type {PendingWrite} from '@/protocols/persistence/types'
import {renderPayloadYaml} from './pendingWritePayloadView'
import commonClasses from "@/common/common.module.css"
import cx from "clsx";

interface Props {
    rows: PendingWrite[]
    onRetry: (row: PendingWrite) => void
}

// Format `createdAt` (Unix ms) for the "Changed At" column. 24h,
// sortable, unambiguous (no AM/PM, no locale-specific month name).
// Matches the existing PendingWritesTable format string.
const CHANGED_AT_FORMAT = 'YYYY-MM-DD HH:mm:ss'

const EMPTY_TESTID = 'write-rows-empty'

// Stateless presentational table for write rows. The orchestrator
// (PendingActivityModal) owns filtering, fetch, retry routing, and
// event listeners. PendingWritesTable only renders — it has no
// Modal, no auto-refresh, no zustand store access, so it can be
// tested and reused in isolation.
//
// Columns mirror the legacy modal's write-column shape (modulo the
// dropped `kind` and merged-upload columns, which now live in
// PendingUploadTable).
export const PendingWritesTable: React.FC<Props> = ({rows, onRetry}) => {
    return (
        <Stack gap={4}>
            <Text className={cx(commonClasses.tgbBorder)} size="sm" fw={500}>Pending writes</Text>
            <Table striped highlightOnHover data-testid="pending-write-table">
                <Table.Thead>
                    <Table.Tr>
                        <Table.Th>id</Table.Th>
                        <Table.Th>Changed At</Table.Th>
                        <Table.Th>write_type</Table.Th>
                        <Table.Th>state</Table.Th>
                        <Table.Th>payload</Table.Th>
                        <Table.Th>Last Error</Table.Th>
                        <Table.Th>Actions</Table.Th>
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {rows.length > 0 ? (
                        rows.map((r) => (
                            <Table.Tr
                                key={`write-${r.id}`}
                                data-testid={`write-row-${r.id}`}
                                data-row-kind="write"
                            >
                                <Table.Td>{r.id}</Table.Td>
                                <Table.Td data-testid={`write-row-${r.id}-changed-at`}>
                                    {dayjs(r.createdAt).format(CHANGED_AT_FORMAT)}
                                </Table.Td>
                                <Table.Td><code>{r.writeType}</code></Table.Td>
                                <Table.Td>
                                    <code>{r.state === 'dead' ? 'failed' : r.state}</code>
                                </Table.Td>
                                <Table.Td>
                                    <Popover position="left" withArrow shadow="md" width={500}
                                             trapFocus={false}>
                                        <Popover.Target>
                                            <Text size="sm" c="dimmed" style={{cursor: 'help'}}>
                                                Click for Details
                                            </Text>
                                        </Popover.Target>
                                        <Popover.Dropdown>
                                            <Code block style={{whiteSpace: 'pre'}}>
                                                {renderPayloadYaml(r)}
                                            </Code>
                                        </Popover.Dropdown>
                                    </Popover>
                                </Table.Td>
                                <Table.Td>{r.lastError ? r.lastError : '(none)'}</Table.Td>
                                <Table.Td>
                                    {r.state === 'dead' ? (
                                        <Button
                                            size="xs"
                                            onClick={() => onRetry(r)}
                                            data-testid={`write-retry-${r.id}`}
                                        >
                                            Retry
                                        </Button>
                                    ) : (
                                        <Text size="sm" c="dimmed">—</Text>
                                    )}
                                </Table.Td>
                            </Table.Tr>
                        ))
                    ) : (
                        <Table.Tr data-testid={EMPTY_TESTID}>
                            <Table.Td colSpan={7}>
                                <Text size="sm" c="dimmed">
                                    No pending writes.
                                </Text>
                            </Table.Td>
                        </Table.Tr>
                    )}
                </Table.Tbody>
            </Table>
        </Stack>
    )
}
